import { useEffect, useRef, useState } from 'react';
import { IconCopy } from '@belong/icons';
import { FAILED_PAYMENTS_STRINGS } from './FailedPayments/failed-payments-strings';

export function CopyToClipboardButton({
  value,
  ariaLabel = FAILED_PAYMENTS_STRINGS['external.copy_label'],
}: {
  value: string;
  ariaLabel?: string;
}) {
  const [feedback, setFeedback] = useState<'copied' | 'error' | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setFeedback(null);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [value]);

  async function handleCopy() {
    let result: 'copied' | 'error';
    try {
      await navigator.clipboard.writeText(value);
      result = 'copied';
    } catch {
      result = 'error';
    }
    setFeedback(result);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(
      () => setFeedback(null),
      result === 'copied' ? 2000 : 4000,
    );
  }

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={handleCopy}
      className="relative inline-flex shrink-0 cursor-pointer border-0 bg-transparent p-0"
    >
      <span
        role="status"
        aria-live="polite"
        className={
          feedback
            ? 'absolute bottom-full right-0 mb-2xs whitespace-nowrap rounded bg-navy px-xs py-2xs text-p1 text-white'
            : 'sr-only'
        }
      >
        {feedback === 'copied'
          ? FAILED_PAYMENTS_STRINGS['wire.copied']
          : feedback === 'error'
            ? FAILED_PAYMENTS_STRINGS['external.copy_error']
            : ''}
      </span>
      <IconCopy
        width={16}
        height={16}
        className={feedback === 'copied' ? 'text-dark-green' : 'text-green'}
      />
    </button>
  );
}
